import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  ArrowLeft,
  FileSpreadsheet,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
  UsersRound,
  X,
} from "lucide-react";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";
import { db } from "../web/src/firebaseClient";
import { importUsersFromFile } from "../web/src/services/importService";

const academicYears = Array.from({ length: 5 }, (_, index) => {
  const startYear = 2026 + index;
  return `${startYear}-${startYear + 1}`;
});

export default function AdminUsersTeams() {
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);

  const [selectedTeamId, setSelectedTeamId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    academicYear: academicYears[0],
    mentorIds: [],
    memberIds: [],
  });

  const [memberSearch, setMemberSearch] = useState("");

  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedAllowedRoles, setSelectedAllowedRoles] = useState({});

  const [roleMessage, setRoleMessage] = useState({
    type: "",
    text: "",
  });

  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const init = async () => {
      const user = getAuth().currentUser;

      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const token = await user.getIdTokenResult();
      const admin = token.claims.role === "admin";

      setIsAdmin(admin);

      if (admin) {
        await fetchData();
      }

      setLoading(false);
    };

    init();
  }, []);

  const fetchData = async () => {
    const usersSnapshot = await getDocs(
      query(collection(db, "users"), orderBy("createdAt", "desc"))
    );

    const teamsSnapshot = await getDocs(
      query(collection(db, "teams"), orderBy("createdAt", "desc"))
    );

    const rolesSnapshot = await getDocs(
      query(collection(db, "userRoles"), orderBy("order", "asc"))
    );

    setUsers(
      usersSnapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }))
    );

    setTeams(
      teamsSnapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }))
    );

    setRoles(
      rolesSnapshot.docs
        .map((item) => ({
          id: item.id,
          ...item.data(),
        }))
        .filter((role) => role.isActive)
    );
  };

  const selectedTeam = useMemo(() => {
    return teams.find((team) => team.id === selectedTeamId) || null;
  }, [teams, selectedTeamId]);

  const mentors = useMemo(() => {
    return users.filter((user) => user.roles?.mentor === true);
  }, [users]);

  const selectedMentors = useMemo(() => {
    return users.filter((user) => form.mentorIds.includes(user.id));
  }, [users, form.mentorIds]);

  const selectedMembers = useMemo(() => {
    return users.filter((user) => form.memberIds.includes(user.id));
  }, [users, form.memberIds]);

  const filteredMembers = useMemo(() => {
    const keyword = memberSearch.trim().toLowerCase();

    if (!keyword) return [];

    return users
      .filter((user) => !form.memberIds.includes(user.id))
      .filter((user) =>
        `${user.fullName || ""} ${user.email || ""}`
          .toLowerCase()
          .includes(keyword)
      )
      .slice(0, 8);
  }, [users, memberSearch, form.memberIds]);

  const filteredUsers = useMemo(() => {
    const keyword = userSearch.trim().toLowerCase();

    if (!keyword) return [];

    return users
      .filter((user) =>
        `${user.fullName || ""} ${user.email || ""}`
          .toLowerCase()
          .includes(keyword)
      )
      .slice(0, 10);
  }, [users, userSearch]);

  const isFormValid =
    form.name.trim() && form.mentorIds.length > 0 && form.memberIds.length > 0;

  const resetCreateForm = () => {
    setSelectedTeamId(null);

    setForm({
      name: "",
      academicYear: academicYears[0],
      mentorIds: [],
      memberIds: [],
    });

    setMemberSearch("");
  };

  const handleSelectTeam = (team) => {
    const oldMentorIds = team.mentorId ? [team.mentorId] : [];

    setSelectedTeamId(team.id);

    setForm({
      name: team.name || "",
      academicYear: team.academicYear || academicYears[0],
      mentorIds: Array.isArray(team.mentorIds)
        ? team.mentorIds
        : oldMentorIds,
      memberIds: Array.isArray(team.memberIds) ? team.memberIds : [],
    });

    setMemberSearch("");
  };

  const handleAddMentor = (mentorId) => {
    setForm((prev) => ({
      ...prev,
      mentorIds: prev.mentorIds.includes(mentorId)
        ? prev.mentorIds
        : [...prev.mentorIds, mentorId],
    }));
  };

  const handleRemoveMentor = (mentorId) => {
    setForm((prev) => ({
      ...prev,
      mentorIds: prev.mentorIds.filter((id) => id !== mentorId),
    }));
  };

  const handleAddMember = (memberId) => {
    setForm((prev) => ({
      ...prev,
      memberIds: prev.memberIds.includes(memberId)
        ? prev.memberIds
        : [...prev.memberIds, memberId],
    }));

    setMemberSearch("");
  };

  const handleRemoveMember = (memberId) => {
    setForm((prev) => ({
      ...prev,
      memberIds: prev.memberIds.filter((id) => id !== memberId),
    }));
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();

    if (!isFormValid) return;

    const payload = {
      name: form.name.trim(),
      academicYear: form.academicYear,
      mentorIds: form.mentorIds,
      memberIds: form.memberIds,
      updatedAt: serverTimestamp(),
    };

    if (selectedTeamId) {
      await updateDoc(doc(db, "teams", selectedTeamId), payload);
    } else {
      await addDoc(collection(db, "teams"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    }

    await fetchData();
    resetCreateForm();
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeamId) return;

    await deleteDoc(doc(db, "teams", selectedTeamId));
    await fetchData();
    resetCreateForm();
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role || "");
    setSelectedAllowedRoles(user.roles || {});
    setUserSearch(user.fullName || "");
    setRoleMessage({ type: "", text: "" });
  };

  const handleAllowedRoleChange = (roleValue, checked) => {
    setSelectedAllowedRoles((prev) => ({
      ...prev,
      [roleValue]: checked,
    }));
  };

  const handleUpdateUserRoles = async () => {
    if (!selectedUser) return;

    try {
      const updatedAllowedRoles = {
        ...selectedAllowedRoles,
      };

      if (selectedRole) {
        updatedAllowedRoles[selectedRole] = true;
      }

      await updateDoc(doc(db, "users", selectedUser.id), {
        role: selectedRole || "",
        roles: updatedAllowedRoles,
        updatedAt: serverTimestamp(),
      });

      await fetchData();

      setSelectedUser((prev) => ({
        ...prev,
        role: selectedRole || "",
        roles: updatedAllowedRoles,
      }));

      setSelectedAllowedRoles(updatedAllowedRoles);

      setRoleMessage({
        type: "success",
        text: "Kullanıcı rolleri başarıyla güncellendi.",
      });
    } catch (error) {
      setRoleMessage({
        type: "error",
        text: "Kullanıcı rolleri güncellenemedi. Lütfen tekrar deneyin.",
      });
    }
  };

  const handleImportFileChange = (e) => {
    const selectedFile = e.target.files?.[0] || null;
    setImportFile(selectedFile);
    setImportError("");
    setImportResult(null);
  };

  const handleImportUsers = async () => {
    setImportError("");
    setImportResult(null);

    if (!importFile) {
      setImportError("Lütfen önce bir Excel dosyası seçin.");
      return;
    }

    try {
      setImportLoading(true);
      const response = await importUsersFromFile(importFile);
      setImportResult(response.report);
      await fetchData();
    } catch (error) {
      console.error("UI IMPORT ERROR:", error);
      setImportError(error.message || "İçe aktarma sırasında hata oluştu.");
    } finally {
      setImportLoading(false);
    }
  };


  if (loading || isAdmin === null) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Bu sayfaya erişim yetkiniz yok
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef5ff]">
      <InnovationHubHeader />

      <section className="p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#1565C0] hover:underline"
            >
              <ArrowLeft size={18} />
              Admin Panele Dön
            </button>

            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8f2ff]">
                <UsersRound className="text-[#1565C0]" size={26} />
              </div>

              <div>
                <h1 className="text-2xl font-semibold text-[#173b7a]">
                  Kullanıcı ve Takım Yönetimi
                </h1>

                <p className="mt-1 text-sm text-gray-500">
                  Kullanıcı rollerini ve takımları yönetebilirsiniz.
                </p>
              </div>
            </div>

            <div className="mt-6 inline-flex rounded-2xl bg-[#eef5ff] p-1">
              <button
                type="button"
                onClick={() => setActiveTab("users")}
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${
                  activeTab === "users"
                    ? "bg-white text-[#1565C0] shadow-sm"
                    : "text-gray-500 hover:text-[#1565C0]"
                }`}
              >
                Kullanıcı Yönetimi
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("teams")}
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${
                  activeTab === "teams"
                    ? "bg-white text-[#1565C0] shadow-sm"
                    : "text-gray-500 hover:text-[#1565C0]"
                }`}
              >
                Takım Yönetimi
              </button>
            </div>
          </div>

          {activeTab === "users" && (
            <div className="space-y-6">
              <div className="rounded-2xl bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#eef5ff] px-3 py-1.5 text-xs font-semibold text-[#1565C0]">
                      <FileSpreadsheet size={15} />
                      Toplu İşlem
                    </div>

                    <h2 className="text-xl font-semibold text-[#173b7a]">
                      Excel'den Toplu Kullanıcı Ekle
                    </h2>

                    <p className="mt-1 text-sm text-gray-500">
                      .xlsx dosyasından birden fazla kullanıcıyı tek seferde sisteme aktarabilirsiniz.
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-dashed border-[#b7d3f4] bg-[#f8fbff] p-5">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleImportFileChange}
                    className="block w-full cursor-pointer rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-[#e8f2ff] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#1565C0] hover:file:bg-[#dcecff]"
                  />

                  {importFile && (
                    <p className="mt-3 text-sm text-gray-500">
                      Seçilen dosya: <span className="font-semibold text-[#173b7a]">{importFile.name}</span>
                    </p>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleImportUsers}
                      disabled={importLoading || !importFile}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f4f99] disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      <Upload size={18} />
                      {importLoading ? "İçe aktarılıyor..." : "İçe Aktar"}
                    </button>
                  </div>

                  {importError && (
                    <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                      {importError}
                    </div>
                  )}

                  {importResult && (
                    <div className="mt-5 rounded-2xl border border-gray-100 bg-white p-5">
                      <h3 className="text-base font-semibold text-[#173b7a]">
                        İçe Aktarma Sonucu
                      </h3>

                      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl bg-[#f8fbff] p-4">
                          <p className="text-xs text-gray-500">Toplam Satır</p>
                          <p className="mt-1 text-xl font-bold text-[#173b7a]">
                            {importResult.totalRows ?? 0}
                          </p>
                        </div>

                        <div className="rounded-xl bg-green-50 p-4">
                          <p className="text-xs text-green-700">Başarılı</p>
                          <p className="mt-1 text-xl font-bold text-green-700">
                            {importResult.successCount ?? 0}
                          </p>
                        </div>

                        <div className="rounded-xl bg-red-50 p-4">
                          <p className="text-xs text-red-700">Başarısız</p>
                          <p className="mt-1 text-xl font-bold text-red-700">
                            {importResult.failedCount ?? 0}
                          </p>
                        </div>
                      </div>

                      <details className="mt-4 rounded-xl border border-gray-100 bg-[#f8fbff] p-4">
                        <summary className="cursor-pointer text-sm font-semibold text-[#1565C0]">
                          Ayrıntılı raporu göster
                        </summary>
                        <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-white p-4 text-xs text-gray-600">
                          {JSON.stringify(importResult, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-white p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-[#173b7a]">
                  Kullanıcı Rolleri
                </h2>

              <div className="mt-6">
                <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                  Kullanıcı Ara
                </label>

                <div className="relative">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setSelectedUser(null);
                      setSelectedRole("");
                      setSelectedAllowedRoles({});
                      setRoleMessage({ type: "", text: "" });
                    }}
                    placeholder="Ad soyad veya e-posta"
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm outline-none focus:border-[#1565C0]"
                  />

                  {filteredUsers.length > 0 && !selectedUser && (
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
                      {filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleSelectUser(user)}
                          className="block w-full px-4 py-3 text-left transition hover:bg-[#f8fbff]"
                        >
                          <span className="block font-medium text-[#173b7a]">
                            {user.fullName}
                          </span>

                          <span className="block text-xs text-gray-500">
                            {user.email}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {selectedUser && (
                <div className="mt-6 rounded-2xl border border-gray-100 bg-[#f8fbff] p-5">
                  <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#173b7a]">
                        {selectedUser.fullName}
                      </h3>

                      <p className="mt-1 text-sm text-gray-500">
                        {selectedUser.email}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUser(null);
                        setUserSearch("");
                        setSelectedRole("");
                        setSelectedAllowedRoles({});
                        setRoleMessage({ type: "", text: "" });
                      }}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-500 transition hover:bg-white"
                    >
                      <X size={16} />
                      Kapat
                    </button>
                  </div>

                  {roleMessage.text && (
                    <div
                      className={`mt-5 rounded-xl px-4 py-3 text-sm font-medium ${
                        roleMessage.type === "success"
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {roleMessage.text}
                    </div>
                  )}

                  <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-5">
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Ana Rol
                    </label>

                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                    >
                      <option value="">Ana rol seçin</option>

                      {roles.map((role) => (
                        <option key={role.id} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-5 rounded-2xl border border-gray-100 bg-white p-5">
                    <label className="mb-3 block text-sm font-semibold text-[#173b7a]">
                      İzin Verilen Roller
                    </label>

                    <div className="space-y-3">
                      {roles.map((role) => {
                        const checked = !!selectedAllowedRoles?.[role.value];

                        return (
                          <label
                            key={role.id}
                            className="flex cursor-pointer items-center justify-between rounded-xl border border-gray-100 bg-[#f8fbff] p-4 transition hover:bg-white"
                          >
                            <div>
                              <p className="text-sm font-semibold text-[#173b7a]">
                                {role.label}
                              </p>

                              <p className="text-xs text-gray-500">
                                {role.value}
                              </p>
                            </div>

                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                handleAllowedRoleChange(
                                  role.value,
                                  e.target.checked
                                )
                              }
                              className="h-5 w-5 accent-[#1565C0]"
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleUpdateUserRoles}
                      disabled={!selectedUser}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f4f99] disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      <Save size={18} />
                      Kullanıcı Rollerini Güncelle
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {activeTab === "teams" && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <aside className="rounded-2xl bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-[#173b7a]">
                    Mevcut Takımlar
                  </h2>

                  <button
                    type="button"
                    onClick={resetCreateForm}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#1565C0] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#0f4f99]"
                  >
                    <Plus size={15} />
                    Yeni
                  </button>
                </div>

                <div className="space-y-3">
                  {teams.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Henüz takım oluşturulmadı.
                    </p>
                  ) : (
                    teams.map((team) => (
                      <button
                        key={team.id}
                        type="button"
                        onClick={() => handleSelectTeam(team)}
                        className={`w-full rounded-xl border p-4 text-left transition ${
                          selectedTeamId === team.id
                            ? "border-[#1565C0] bg-[#eef5ff]"
                            : "border-gray-100 bg-[#f8fbff] hover:bg-white"
                        }`}
                      >
                        <p className="font-semibold text-[#173b7a]">
                          {team.name}
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          {team.academicYear || "-"}
                        </p>

                        <p className="mt-2 text-xs text-gray-500">
                          Mentör Sayısı:{" "}
                          {Array.isArray(team.mentorIds)
                            ? team.mentorIds.length
                            : 0}
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          Üye Sayısı:{" "}
                          {Array.isArray(team.memberIds)
                            ? team.memberIds.length
                            : 0}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </aside>

              <section className="rounded-2xl bg-white p-6 shadow-sm lg:col-span-2">
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-[#173b7a]">
                      {selectedTeam
                        ? "Takım Bilgileri"
                        : "Yeni Takım Oluştur"}
                    </h2>

                    <p className="mt-1 text-sm text-gray-500">
                      {selectedTeam
                        ? "Takım bilgilerini güncelleyebilirsiniz."
                        : "Yeni takım oluşturabilirsiniz."}
                    </p>
                  </div>

                  {selectedTeam && (
                    <button
                      type="button"
                      onClick={handleDeleteTeam}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-100 px-3 py-2 text-sm font-medium text-red-500 transition hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                      Sil
                    </button>
                  )}
                </div>

                <form onSubmit={handleSaveTeam} className="space-y-5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                        Takım İsmi
                      </label>

                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Takım ismi"
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                        Eğitim Öğretim Yılı
                      </label>

                      <select
                        value={form.academicYear}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            academicYear: e.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                      >
                        {academicYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Mentörler
                    </label>

                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddMentor(e.target.value);
                        }
                      }}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                    >
                      <option value="">Mentör seçin</option>

                      {mentors
                        .filter(
                          (mentor) => !form.mentorIds.includes(mentor.id)
                        )
                        .map((mentor) => (
                          <option key={mentor.id} value={mentor.id}>
                            {mentor.email}
                          </option>
                        ))}
                    </select>

                    {selectedMentors.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {selectedMentors.map((mentor) => (
                          <div
                            key={mentor.id}
                            className="flex items-center justify-between rounded-xl border border-gray-100 bg-[#f8fbff] p-3"
                          >
                            <div>
                              <p className="text-sm font-semibold text-[#173b7a]">
                                {mentor.fullName}
                              </p>

                              <p className="text-xs text-gray-500">
                                {mentor.email}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveMentor(mentor.id)}
                              className="rounded-lg p-2 text-gray-400 transition hover:bg-white hover:text-red-500"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Takım Üyeleri
                    </label>

                    <div className="relative">
                      <Search
                        size={17}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        type="text"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Üye adı veya e-posta ara"
                        className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm outline-none focus:border-[#1565C0]"
                      />
                    </div>

                    {memberSearch.trim() && (
                      <div className="mt-3 space-y-2 rounded-xl border border-gray-100 bg-[#f8fbff] p-3">
                        {filteredMembers.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            Uygun kullanıcı bulunamadı.
                          </p>
                        ) : (
                          filteredMembers.map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center justify-between gap-3 rounded-xl bg-white p-3"
                            >
                              <div>
                                <p className="text-sm font-semibold text-[#173b7a]">
                                  {user.fullName}
                                </p>

                                <p className="text-xs text-gray-500">
                                  {user.email}
                                </p>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleAddMember(user.id)}
                                className="inline-flex items-center gap-1 rounded-lg bg-[#1565C0] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#0f4f99]"
                              >
                                <Plus size={14} />
                                Ekle
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    <div className="mt-4 space-y-3">
                      {selectedMembers.length === 0 ? (
                        <p className="rounded-xl bg-[#f8fbff] p-4 text-sm text-gray-500">
                          Henüz üye eklenmedi.
                        </p>
                      ) : (
                        selectedMembers.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between rounded-xl border border-gray-100 bg-[#f8fbff] p-3"
                          >
                            <div>
                              <p className="text-sm font-semibold text-[#173b7a]">
                                {member.fullName}
                              </p>

                              <p className="text-xs text-gray-500">
                                {member.email}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveMember(member.id)}
                              className="rounded-lg p-2 text-gray-400 transition hover:bg-white hover:text-red-500"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={!isFormValid}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f4f99] disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      <Save size={18} />

                      {selectedTeam ? "Takımı Güncelle" : "Takım Oluştur"}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          )}
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}